import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Divider, Drawer,
    FormControl, IconButton, InputAdornment, Menu, MenuItem,
    Paper, Select, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import Layout from './Layout';
import PageHeader from './PageHeader';
import InventoryService from '../services/InventoryService';
import { useToast } from '../contexts/ToastContext';

const BRAND = '#427898';

const EMPTY_FORM = {
    name: '', description: '', category: '', unitOfMeasure: '',
    sellPrice: '', minStockLevel: '', storageLocation: '',
};

function stockMeta(item) {
    if (item.currentStock <= 0)
        return { label: 'Out of stock', color: '#EF4444', bg: '#FEF2F2', strip: '#EF4444' };
    if (item.minStockLevel > 0 && item.currentStock <= item.minStockLevel)
        return { label: 'Low stock', color: '#F59E0B', bg: '#FFFBEB', strip: '#F59E0B' };
    return { label: 'In stock', color: '#16A34A', bg: '#F0FDF4', strip: '#22C55E' };
}

/* ── WorkspaceDrawer ─────────────────────────────────────────────────────── */
const WorkspaceDrawer = ({ open, onClose, title, subtitle, saveLabel, onSave, saving, children }) => (
    <Drawer
        anchor="right" open={open} onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 480 }, boxShadow: '-4px 0 32px rgba(15,23,42,0.12)' } }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.08)' } } }}
    >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ height: 3, bgcolor: BRAND, flexShrink: 0 }} />
            <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #EEF2F6', flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                    <Typography fontWeight={700} fontSize={16} color="#0F172A">{title}</Typography>
                    {subtitle && <Typography fontSize={12} color="#94A3B8" mt={0.3}>{subtitle}</Typography>}
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8', mt: -0.25 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
                {children}
            </Box>
            <Box sx={{ px: 3, py: 2.5, flexShrink: 0, borderTop: '1px solid #EEF2F6', bgcolor: '#FAFBFC', display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B', fontWeight: 500 }}>Cancel</Button>
                <Button variant="contained" onClick={onSave} disabled={saving}
                    sx={{ bgcolor: BRAND, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', px: 2.5, '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}>
                    {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : saveLabel}
                </Button>
            </Box>
        </Box>
    </Drawer>
);

/* ── Field group label ───────────────────────────────────────────────────── */
const FieldLabel = ({ children }) => (
    <Typography fontSize={11} fontWeight={600} color="#94A3B8" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, mb: 1 }}>
        {children}
    </Typography>
);

/* ── Inventory item card ─────────────────────────────────────────────────── */
function ItemCard({ item, onEdit, onDelete }) {
    const navigate = useNavigate();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const meta = stockMeta(item);

    return (
        <Paper elevation={0}
            sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden', display: 'flex', cursor: 'pointer', transition: 'box-shadow 0.15s', '&:hover': { boxShadow: '0 2px 12px rgba(15,23,42,0.06)' } }}
            onClick={() => navigate(`/inventory/${item.id}`)}
        >
            {/* Stock status strip */}
            <Box sx={{ width: 4, bgcolor: meta.strip, flexShrink: 0 }} />

            {/* Content */}
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', px: 2.5, py: 2, gap: 3, minWidth: 0 }}>
                {/* Icon + name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: '2 1 0', minWidth: 0 }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Inventory2OutlinedIcon sx={{ fontSize: 17, color: '#64748B' }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={600} fontSize={14} color="#0F172A" noWrap>{item.name}</Typography>
                        {item.category
                            ? <Chip label={item.category} size="small" sx={{ mt: 0.25, height: 18, fontSize: 11, bgcolor: '#F1F5F9', color: '#475569', borderRadius: '5px', '& .MuiChip-label': { px: 1 } }} />
                            : <Typography fontSize={11} color="#CBD5E1">No category</Typography>}
                    </Box>
                </Box>

                {/* Stock */}
                <Box sx={{ flex: '1 1 0', minWidth: 64 }}>
                    <Typography fontSize={10} fontWeight={600} color="#CBD5E1" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>Stock</Typography>
                    <Typography fontSize={14} fontWeight={700} color={meta.strip} lineHeight={1.1}>
                        {item.currentStock}
                        {item.unitOfMeasure && <Typography component="span" fontSize={11} fontWeight={400} color="#94A3B8" ml={0.5}>{item.unitOfMeasure}</Typography>}
                    </Typography>
                </Box>

                {/* Price */}
                <Box sx={{ flex: '1 1 0', minWidth: 64, display: { xs: 'none', sm: 'block' } }}>
                    <Typography fontSize={10} fontWeight={600} color="#CBD5E1" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>Sell price</Typography>
                    <Typography fontSize={13} color="#334155">{item.sellPrice > 0 ? `CHF ${item.sellPrice.toFixed(2)}` : '—'}</Typography>
                </Box>

                {/* Location */}
                <Box sx={{ flex: '1 1 0', minWidth: 80, display: { xs: 'none', md: 'block' } }}>
                    <Typography fontSize={10} fontWeight={600} color="#CBD5E1" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>Location</Typography>
                    <Typography fontSize={13} color="#334155" noWrap>{item.storageLocation || '—'}</Typography>
                </Box>

                {/* Status chip */}
                <Chip label={meta.label} size="small" sx={{ fontSize: 11, fontWeight: 700, bgcolor: meta.bg, color: meta.color, borderRadius: '6px', height: 22, flexShrink: 0 }} />

                {/* Menu */}
                <IconButton size="small"
                    onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget); }}
                    sx={{ color: '#CBD5E1', flexShrink: 0, borderRadius: 1, '&:hover': { bgcolor: '#F1F5F9', color: '#475569' } }}
                >
                    <MoreHorizIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Context menu */}
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
                PaperProps={{ elevation: 0, sx: { border: '1px solid #EEF2F6', borderRadius: 1.5, boxShadow: '0 4px 20px rgba(15,23,42,0.10)', minWidth: 168 } }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={() => { navigate(`/inventory/${item.id}`); setMenuAnchor(null); }}
                    sx={{ fontSize: 13, gap: 1.5, color: '#334155', py: 1 }}>
                    <OpenInNewIcon fontSize="small" sx={{ color: '#64748B' }} />
                    Batches & Transactions
                </MenuItem>
                <MenuItem onClick={() => { onEdit(item); setMenuAnchor(null); }}
                    sx={{ fontSize: 13, gap: 1.5, color: '#334155', py: 1 }}>
                    Edit
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={() => { onDelete(item); setMenuAnchor(null); }}
                    sx={{ fontSize: 13, gap: 1.5, color: '#EF4444', py: 1 }}>
                    <DeleteOutlineIcon fontSize="small" />
                    Delete
                </MenuItem>
            </Menu>
        </Paper>
    );
}

/* ── Main page ───────────────────────────────────────────────────────────── */
const InventoryPage = () => {
    const { showToast } = useToast();
    const navigate = useNavigate();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        InventoryService.getAll()
            .then(data => setItems(data || []))
            .catch(() => showToast('Failed to load inventory', 'error'))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const categories = [...new Set(items.map(i => i.category).filter(Boolean))].sort();

    const filtered = items.filter(item => {
        const term = searchTerm.toLowerCase();
        const matchSearch = !term
            || item.name?.toLowerCase().includes(term)
            || item.category?.toLowerCase().includes(term)
            || item.storageLocation?.toLowerCase().includes(term);
        const matchCategory = !categoryFilter || item.category === categoryFilter;
        return matchSearch && matchCategory;
    });

    /* ── Drawer ── */
    const openCreate = () => {
        setEditingItem(null);
        setForm(EMPTY_FORM);
        setDrawerOpen(true);
    };

    const openEdit = (item) => {
        setEditingItem(item);
        setForm({
            name: item.name || '',
            description: item.description || '',
            category: item.category || '',
            unitOfMeasure: item.unitOfMeasure || '',
            sellPrice: item.sellPrice ?? '',
            minStockLevel: item.minStockLevel ?? '',
            storageLocation: item.storageLocation || '',
        });
        setDrawerOpen(true);
    };

    const closeDrawer = () => { setDrawerOpen(false); setEditingItem(null); setForm(EMPTY_FORM); };

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const handleSave = async () => {
        if (!form.name.trim()) { showToast('Name is required', 'error'); return; }
        setSaving(true);
        const payload = {
            name: form.name.trim(),
            description: form.description || null,
            category: form.category || null,
            unitOfMeasure: form.unitOfMeasure || null,
            sellPrice: parseFloat(form.sellPrice) || 0,
            minStockLevel: parseFloat(form.minStockLevel) || 0,
            storageLocation: form.storageLocation || null,
        };
        try {
            if (editingItem) {
                const updated = await InventoryService.update(editingItem.id, payload);
                setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
                showToast('Item updated', 'success');
            } else {
                const created = await InventoryService.create(payload);
                setItems(prev => [...prev, created]);
                showToast('Item created', 'success');
            }
            closeDrawer();
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to save item', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (item) => {
        try {
            await InventoryService.delete(item.id);
            setItems(prev => prev.filter(i => i.id !== item.id));
            showToast('Item deleted', 'warning');
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to delete item', 'error');
        }
    };

    return (
        <Layout>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <PageHeader
                    title="Inventory"
                    action={
                        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
                            sx={{ bgcolor: BRAND, borderRadius: 1.5, textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: 'none', filter: 'brightness(0.9)' } }}>
                            Add Item
                        </Button>
                    }
                />

                {/* Toolbar */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <TextField
                        placeholder="Search items…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        size="small"
                        sx={{ flex: '1 1 220px', maxWidth: 360, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 1.5 } }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: '#CBD5E1' }} /></InputAdornment>
                        }}
                    />
                    {categories.length > 0 && (
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <Select
                                value={categoryFilter}
                                onChange={e => setCategoryFilter(e.target.value)}
                                displayEmpty
                                startAdornment={<TuneIcon sx={{ fontSize: 16, color: '#94A3B8', mr: 0.5 }} />}
                                sx={{ bgcolor: '#fff', borderRadius: 1.5, fontSize: 13 }}
                            >
                                <MenuItem value="" sx={{ fontSize: 13 }}>All categories</MenuItem>
                                {categories.map(c => <MenuItem key={c} value={c} sx={{ fontSize: 13 }}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                </Box>

                {/* Loading */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress size={28} sx={{ color: BRAND }} />
                    </Box>
                )}

                {/* Empty state */}
                {!loading && filtered.length === 0 && (
                    <Paper elevation={0} sx={{ border: '1.5px dashed #E2E8F0', borderRadius: 2, p: 6, textAlign: 'center', bgcolor: '#FAFBFC' }}>
                        <Box sx={{ width: 52, height: 52, borderRadius: 2, bgcolor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                            <Inventory2OutlinedIcon sx={{ fontSize: 26, color: '#CBD5E1' }} />
                        </Box>
                        <Typography fontWeight={600} fontSize={14} color="#64748B" mb={0.5}>
                            {searchTerm || categoryFilter ? 'No items match your filters' : 'No inventory items yet'}
                        </Typography>
                        <Typography fontSize={13} color="#94A3B8" mb={2.5}>
                            {searchTerm || categoryFilter ? 'Try adjusting your search or filter' : 'Add your first item to start tracking stock'}
                        </Typography>
                        {!searchTerm && !categoryFilter && (
                            <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}
                                sx={{ textTransform: 'none', fontSize: 13, borderColor: BRAND, color: BRAND, borderRadius: 1.5 }}>
                                Add Item
                            </Button>
                        )}
                    </Paper>
                )}

                {/* Item cards */}
                {!loading && filtered.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {filtered.map(item => (
                            <ItemCard key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} />
                        ))}
                        <Typography fontSize={12} color="#94A3B8" textAlign="right" mt={0.5}>
                            {filtered.length} item{filtered.length !== 1 ? 's' : ''}
                            {(searchTerm || categoryFilter) && ` · ${items.length} total`}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Create / Edit drawer */}
            <WorkspaceDrawer
                open={drawerOpen}
                onClose={closeDrawer}
                title={editingItem ? 'Edit Item' : 'New Inventory Item'}
                subtitle={editingItem ? editingItem.name : undefined}
                saveLabel={editingItem ? 'Save Changes' : 'Create Item'}
                onSave={handleSave}
                saving={saving}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box>
                        <FieldLabel>Name *</FieldLabel>
                        <TextField fullWidth size="small" placeholder="e.g. Cable 10m" autoFocus
                            value={form.name} onChange={e => set('name', e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                    </Box>

                    <Box>
                        <FieldLabel>Description</FieldLabel>
                        <TextField fullWidth size="small" multiline rows={2} placeholder="Optional notes…"
                            value={form.description} onChange={e => set('description', e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Box>
                            <FieldLabel>Category</FieldLabel>
                            <TextField fullWidth size="small" placeholder="e.g. Electrical"
                                value={form.category} onChange={e => set('category', e.target.value)}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                        </Box>
                        <Box>
                            <FieldLabel>Unit of measure</FieldLabel>
                            <TextField fullWidth size="small" placeholder="e.g. kg, m², Stk"
                                value={form.unitOfMeasure} onChange={e => set('unitOfMeasure', e.target.value)}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                        </Box>
                    </Box>

                    <Divider />

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <Box>
                            <FieldLabel>Sell price (CHF)</FieldLabel>
                            <TextField fullWidth size="small" type="number" placeholder="0.00"
                                value={form.sellPrice} onChange={e => set('sellPrice', e.target.value)}
                                inputProps={{ min: 0, step: '0.01' }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                        </Box>
                        <Box>
                            <FieldLabel>Min. stock level</FieldLabel>
                            <TextField fullWidth size="small" type="number" placeholder="Alert threshold"
                                value={form.minStockLevel} onChange={e => set('minStockLevel', e.target.value)}
                                inputProps={{ min: 0 }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                        </Box>
                    </Box>

                    <Box>
                        <FieldLabel>Storage location</FieldLabel>
                        <TextField fullWidth size="small" placeholder="e.g. Shelf A-12"
                            value={form.storageLocation} onChange={e => set('storageLocation', e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
                    </Box>
                </Box>
            </WorkspaceDrawer>
        </Layout>
    );
};

export default InventoryPage;
